/**
 * AppModeContext - Gère le switch entre Mode Shop et Mode Réseau
 * 
 * Mode Shop (AbracadaBati) : Artisan | Marketplace | Publier | Emplois | Réseau
 * Mode Réseau (Abracadaworld) : Actus | Cinéma | Publier | Page Pro | Shop
 */

import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Les deux modes de l'application
export const APP_MODES = {
  SHOP: 'shop',      // AbracadaBati - Écosystème commerce
  RESEAU: 'reseau',  // Abracadaworld - Réseau social
};

// Routes qui déclenchent automatiquement le mode Réseau
const RESEAU_ROUTES = [
  '/reseau',
  '/reseau/trending',
  '/reseau/pages',
  '/reseau/cinema',
  '/reseau/galerie',
  '/reseau/salon',
  '/reseau/evenements',
  '/reseau/actus',
  '/reseau/profil',
  '/reseau/messages',
];

// Routes qui déclenchent automatiquement le mode Shop
const SHOP_ROUTES = [
  '/artisans',
  '/marketplace',
  '/emploi',
  '/cockpit-pro',
  '/cockpit-particulier',
  '/produits',
  '/decrire-projet',
];

const AppModeContext = createContext({
  mode: APP_MODES.SHOP,
  setMode: () => {},
  toggleMode: () => {},
  isReseauMode: false,
  isShopMode: true,
});

export function AppModeProvider({ children }) {
  const [mode, setMode] = useState(APP_MODES.SHOP);
  const location = useLocation();

  // Détecter automatiquement le mode selon la route
  useEffect(() => {
    const path = location.pathname;
    
    // Vérifier si on est sur une route Réseau
    const isReseauRoute = RESEAU_ROUTES.some(route => path.startsWith(route));
    if (isReseauRoute) {
      setMode(APP_MODES.RESEAU);
      return;
    }
    
    // Vérifier si on est sur une route Shop
    const isShopRoute = SHOP_ROUTES.some(route => path.startsWith(route));
    if (isShopRoute) {
      setMode(APP_MODES.SHOP);
      return;
    }
    
    // Garder le mode actuel pour les routes neutres (accueil, etc.)
  }, [location.pathname]);

  const toggleMode = () => {
    setMode(prev => prev === APP_MODES.SHOP ? APP_MODES.RESEAU : APP_MODES.SHOP);
  };

  const value = {
    mode,
    setMode,
    toggleMode,
    isReseauMode: mode === APP_MODES.RESEAU,
    isShopMode: mode === APP_MODES.SHOP,
  };

  return (
    <AppModeContext.Provider value={value}>
      {children}
    </AppModeContext.Provider>
  );
}

export function useAppMode() {
  const context = useContext(AppModeContext);
  if (!context) {
    throw new Error('useAppMode must be used within AppModeProvider');
  }
  return context;
}

export default AppModeContext;
